import { Button } from "@/components/button/Button"
import { EmptyScreen } from "@/components/emptyScreen/EmptyScreen"
import { messagesApi } from "@/fetch/api/messages"
import { formatAttachmentUrl } from "@/lib/formatAttachmentUrl"
import { formatTime } from "@/lib/formatTime"
import { useMessages } from "@/providers/MessagesProvider"
import {
  MessageAttachment,
  MessagePerRecipientWithAttahcments,
} from "src/types"
import styled from "styled-components"
import pdfDataPreview from "../../admin/notifications/pdfIconDataUri"
import React from "react"

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-grow: 1;
  overflow-y: auto;
  max-width: 600px;
  padding: 0 20px;
  margin: 0 auto;
`

const Title = styled.h2`
  font-size: 20px;
  font-weight: 500;
`

const MessageCard = styled.div<{ $unread: boolean }>`
  background: #fafcfe;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  ${(p) => (p.$unread ? "border: 1px solid #8C5FEC;" : "")}
`

const MessageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
`
const HeaderBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  img {
    width: 24px;
    height: 24px;
  }
`

const DateTime = styled.div`
  font-size: 14px;
  color: #999;
  white-space: nowrap;
`

const StyledFileLink = styled.a`
  text-decoration: none;
  gap: 4px;
  color: var(--iris);
  font-size: 12px;
  font-weight: 500;
  &:hover {
    color: var(--iris-hover);
  }
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 120px;
  border: 1px solid var(--accent);
  border-radius: 8px;
  padding: 8px;
  background: #fafcfe;

  img {
    width: 120px;
  }
`

export function extractHrefs(line: string) {
  const urlRegex = /(https?:\/\/[^\s]+)/g

  const parts = line.split(urlRegex)

  return (
    <span>
      {parts.map((part, index) => {
        if (part.match(/^https?:\/\//)) {
          return (
            <a
              key={index}
              href={part}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "var(--iris)" }}
            >
              {part}
            </a>
          )
        }

        return <React.Fragment key={index}>{part}</React.Fragment>
      })}
    </span>
  )
}

const MessageContent = ({
  content,
  attachments,
}: {
  content: string
  attachments?: MessageAttachment[]
}) => {
  const lines = content
    .split(/\r\n/g)
    .map((line, i) => <p key={i}>{extractHrefs(line)}</p>)

  function openPreview(url: string) {
    window.open(url, "_blank")
  }

  return (
    <>
      {lines}
      {attachments?.map((attach) => {
        switch (attach.mimeType) {
          case "image/jpeg":
          case "image/png": {
            const url = formatAttachmentUrl(attach.url)
            return (
              <>
                <img src={url} alt="" key={attach.id} />
                <Button variant="inline" onClick={() => openPreview(url)}>
                  Atvērt attēlu pilnā izmērā
                </Button>
              </>
            )
          }
          case "application/pdf":
          case "application/x-pdf": {
            return (
              <StyledFileLink
                href={formatAttachmentUrl(attach.url)}
                download={attach.name}
                target="_blank"
                rel="noopener noreferrer"
                key={attach.id}
                title={attach.name}
              >
                <img src={pdfDataPreview} alt={attach.name} />
                <span>{attach.name}</span>
              </StyledFileLink>
            )
          }
          default: {
            return null
          }
        }
      })}
    </>
  )
}

export const Notifications = () => {
  const { messages, updateMessages } = useMessages()

  async function markAsRead(id: number) {
    try {
      await messagesApi.markAsRead(id)
      updateMessages()
    } catch {
      //
    }
  }

  return (
    <Container>
      <Title>Ziņojumi</Title>
      {messages.length === 0 ? (
        <EmptyScreen title="Šobrīd nav ziņojumu" />
      ) : (
        messages.map((message) => {
          return (
            <MessageCard key={message.id} $unread={!message.readAt}>
              <MessageHeader>
                <HeaderBlock>
                  <DateTime>{formatTime(message.createdAt)}</DateTime>
                </HeaderBlock>
                {!message.readAt && (
                  <Button
                    variant="inline"
                    onClick={() => markAsRead(message.messageId)}
                  >
                    Atzīmēt kā izlasītu
                  </Button>
                )}
              </MessageHeader>
              <MessageContent
                content={message.content}
                attachments={message.attachments}
              />
            </MessageCard>
          )
        })
      )}
    </Container>
  )
}
